import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import {
    QuestionnaireShell,
    SubmittingScreen,
    SuccessScreen,
} from '@rocco/components/questionnaire';
import type { SuccessStep } from '@rocco/components/questionnaire';
import { Spinner } from '@rocco/components/spinner';
import { TwoPaneModal } from '@rocco/components/two-pane-modal';
import { useUserLoggedIn } from '@modules/user/hook/use-user';
import { isReturningPasskeyDevice } from '~/modules/user/passkey/passkey-device-match';
import { AuthModalBrandPane } from '../user/component/auth-modal/AuthModalBrandPane';
import { getAuthBrandStatement } from '../user/component/auth-modal/auth-intent-copy';
import { GATE_PANE_BRAND_DEFAULT } from '../user/auth-gate/gate-pane-image';

import { useAuthGateSegment } from '../user/auth-gate/use-auth-gate-segment';
import { useQuestionnaire } from '../questionnaire';
import type {
    QuestionnaireDefinition,
    QuestionnaireEvent,
} from '../questionnaire';
import type { MatchAnswers } from './answers';
import { createInitialAnswers } from './answers';
import { formatBriefReference } from './brief-reference';
import type { FiledBrief } from './submit-brief';
import { submitGetMatchedBrief } from './submit-brief';

export interface GetMatchedOutcomeCopy {
    heading: string;
    body: ReactNode;
    steps: SuccessStep[];
    footnote?: ReactNode;
    actionLabel: string;
}

export interface GetMatchedQuestionnaireProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    definition: QuestionnaireDefinition<MatchAnswers>;
    outcome: GetMatchedOutcomeCopy;
    /** Brand mark for the top-left corner. */
    logo?: ReactNode;
    /** Seeds the brief before it opens. */
    initialAnswers?: Partial<MatchAnswers>;
    /**
     * Answers that may only arrive after the brief is already open — what the
     * reader told us about themselves last time, say.
     *
     * Applied once, and only over a field they have not answered themselves.
     * Something they have already chosen is never overwritten by something we
     * looked up.
     */
    deferredAnswers?: Partial<MatchAnswers>;
    onEvent?: (event: QuestionnaireEvent) => void;
    /**
     * Where the reference goes when followed, if anywhere.
     *
     * Supplied rather than decided here: whether that destination is reachable
     * is a question about the site, and this component should not have to know
     * the answer to render a confirmation.
     */
    buildReferenceHref?: (
        submissionId: string,
        buildHubURLSegment: string | null,
    ) => string | undefined;
    /**
     * Something the opening step depends on is still being looked up, so the
     * questions must not start yet.
     *
     * Held on the account wall rather than shown and then corrected: the step
     * order itself changes with the answer, and a reader moved off a question
     * they have already read has been told they were in the wrong place.
     */
    settling?: boolean;
    /**
     * Swapped out in tests. Returns the filed brief where there is one, so the
     * confirmation can name the request and link to the brief's own page.
     */
    submit?: (answers: MatchAnswers) => Promise<FiledBrief | null | void>;
}

/**
 * Get Matched as a full-screen questionnaire, with the account wall first.
 *
 * The wall itself is the ordinary auth modal over the page the reader is on,
 * not the takeover. Covering the whole screen to ask for an account, before
 * they have answered anything or seen what they are agreeing to, is a heavier
 * gesture than signing in deserves — the takeover starts once they are in and
 * actually answering.
 *
 * Asking for the account before the questions is what removes the machinery the
 * old order needed. A guest eight questions deep whose social login navigated
 * the page away had a brief to preserve, so there was session storage with a
 * lifetime, files in IndexedDB, single-use detection of the return trip, and an
 * auto-submit primed to fire on the far side. A reader who signs in before
 * answering anything has nothing to lose, so none of that exists here.
 */
/** Matches the auth segment's own `deciding` pause, for the same reason. */
const SettlingHold = () => (
    <div className="flex min-h-75 flex-1 items-center justify-center">
        <Spinner className="size-9 text-[#828282]" />
    </div>
);

export const GetMatchedQuestionnaire = ({
    open,
    onOpenChange,
    definition,
    outcome,
    logo,
    initialAnswers,
    deferredAnswers,
    onEvent,
    buildReferenceHref,
    settling = false,
    submit = submitGetMatchedBrief,
}: GetMatchedQuestionnaireProps) => {
    const { isLoggedIn } = useUserLoggedIn();

    // Kept here rather than in the engine: the reference is the shape of this
    // particular confirmation, not something a questionnaire has in general.
    const [filed, setFiled] = useState<FiledBrief | null>(null);
    const handleSubmit = useCallback(
        async (answers: MatchAnswers) => {
            const brief = await submit(answers);
            if (brief) setFiled(brief);
        },
        [submit],
    );

    const questionnaire = useQuestionnaire<MatchAnswers>({
        definition,
        initialAnswers: {
            ...createInitialAnswers(
                definition.id.includes('product') ? 'product' : 'professional',
            ),
            ...initialAnswers,
        },
        isAuthenticated: isLoggedIn,
        onEvent,
        onSubmit: handleSubmit,
    });

    const { completeAuth, phase } = questionnaire;

    // Seeded once, over blanks only. The lookup can land after the reader has
    // started, and overwriting an answer they just gave with one we found would
    // be worse than never having looked.
    const seeded = useRef(false);
    useEffect(() => {
        if (seeded.current || !deferredAnswers) return;
        seeded.current = true;

        const blanks = Object.fromEntries(
            Object.entries(deferredAnswers).filter(([key, value]) => {
                const current =
                    questionnaire.answers[key as keyof MatchAnswers];
                return Boolean(value) && !current;
            }),
        );

        if (Object.keys(blanks).length > 0) {
            questionnaire.update(blanks as Partial<MatchAnswers>);
        }
    }, [deferredAnswers, questionnaire]);

    // Auth finishing and the questions starting are two events here, not one.
    // The reader waits out the remaining lookup on the wall they are already
    // looking at, so signing in leads straight to the right first question
    // instead of to a question that changes under them.
    const [authResolved, setAuthResolved] = useState(false);
    const gate = useAuthGateSegment({
        authSource: 'getMatched',
        onResolved: useCallback(() => setAuthResolved(true), []),
    });

    const enteredQuestions = useRef(false);
    useEffect(() => {
        if (!authResolved || settling || enteredQuestions.current) return;
        enteredQuestions.current = true;
        completeAuth();
    }, [authResolved, settling, completeAuth]);

    // The wall is raised once per visit to it. `open` is not enough of a guard:
    // several triggers can mount this surface on one page, and the segment
    // would otherwise be re-entered on every render that keeps the phase.
    const raisedRef = useRef(false);
    useEffect(() => {
        if (!open || phase !== 'auth' || raisedRef.current) return;
        raisedRef.current = true;
        gate.open();
    }, [open, phase, gate]);

    const handleOpenChange = useCallback(
        (next: boolean) => {
            if (!next) {
                questionnaire.abandon();
                gate.close();
                raisedRef.current = false;
            }
            onOpenChange(next);
        },
        [questionnaire, gate, onOpenChange],
    );

    const { step, answers, update, next } = questionnaire;

    const resolveCopy = (
        value: string | ((answers: MatchAnswers) => string) | undefined,
    ) => (typeof value === 'function' ? value(answers) : value);

    if (phase === 'auth') {
        return (
            <TwoPaneModal
                bodyPadding="roomy"
                corners="rounded"
                onOpenChange={handleOpenChange}
                open={open}
                pane={
                    <AuthModalBrandPane
                        image={GATE_PANE_BRAND_DEFAULT}
                        statement={getAuthBrandStatement(
                            'getMatched',
                            isReturningPasskeyDevice(),
                        )}
                    />
                }
                title={definition.title}
            >
                {authResolved ? <SettlingHold /> : gate.element}
            </TwoPaneModal>
        );
    }

    if (phase === 'submitting') {
        return (
            <QuestionnaireShell
                busy
                layout="centred"
                logo={logo}
                onOpenChange={handleOpenChange}
                open={open}
                stepKey="submitting"
                title={definition.title}
            >
                <SubmittingScreen heading="Submitting your request" />
            </QuestionnaireShell>
        );
    }

    if (phase === 'done') {
        return (
            <QuestionnaireShell
                layout="centred"
                logo={logo}
                onOpenChange={handleOpenChange}
                open={open}
                stepKey="done"
                title={definition.title}
            >
                <SuccessScreen
                    actionLabel={outcome.actionLabel}
                    body={outcome.body}
                    footnote={outcome.footnote}
                    heading={outcome.heading}
                    onAction={() => handleOpenChange(false)}
                    reference={
                        filed
                            ? formatBriefReference(filed.submissionId)
                            : undefined
                    }
                    referenceHref={
                        filed
                            ? buildReferenceHref?.(
                                  filed.submissionId,
                                  filed.buildHubURLSegment,
                              )
                            : undefined
                    }
                    steps={outcome.steps}
                />
            </QuestionnaireShell>
        );
    }

    // Already signed in, and the lookup has not landed yet — there is no wall
    // to wait on, so the takeover holds instead. Not `busy`: this is a wait on
    // us, and the reader keeps every way out of it.
    if (settling) {
        return (
            <QuestionnaireShell
                layout="centred"
                logo={logo}
                onOpenChange={handleOpenChange}
                open={open}
                stepKey="settling"
                title={definition.title}
            >
                <SettlingHold />
            </QuestionnaireShell>
        );
    }

    if (!step) return null;

    return (
        <QuestionnaireShell
            canContinue={questionnaire.canContinue}
            columnWidth={step.columnWidth}
            heading={resolveCopy(step.heading)}
            logo={logo}
            microLabel={step.microLabel}
            onBack={questionnaire.back}
            onContinue={next}
            onOpenChange={handleOpenChange}
            open={open}
            paneImage={definition.paneImage}
            progressLabel={definition.progressLabel}
            showBack={!questionnaire.isFirstStep}
            step={questionnaire.stepNumber}
            stepKey={step.key}
            subheading={resolveCopy(step.subheading)}
            title={definition.title}
            totalSteps={questionnaire.totalSteps}
        >
            {questionnaire.submitError ? (
                <p
                    className="mb-4 rounded-2 bg-red-50 px-4 py-3 font-sans text-3.5 text-red-600"
                    role="alert"
                >
                    {questionnaire.submitError}
                </p>
            ) : null}
            {step.render({ answers, next, update })}
        </QuestionnaireShell>
    );
};
